import { ChevronLeft, Plus } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  AssignmentForm,
  AssignmentList,
  type AssignmentRole,
  type Client,
  type CreateAssignmentInput,
  type Matter,
  type UserAssignment,
} from "@lexiun/org-admin";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { usePlatformApi } from "../hooks/usePlatformApi";
import { PlatformApiError } from "../lib/platform-api";
import {
  matterRowToUserAssignment,
  toInheritedFromClient,
} from "../lib/assignment-mappers";

/**
 * Matter-level assignments admin page (Phase D/D4).
 *
 * Mounted at `/orgs/:orgId/clients/:clientId/matters/:matterId/assignments`.
 * Fetches direct `MatterUserAssignment` rows AND the parent
 * `ClientUserAssignment` rows, composes the union on the client so
 * `AssignmentList` renders the cascade UX: direct rows are editable,
 * client-inherited rows are read-only with an "inherited from
 * client: {name}" badge per the broadening plan's access UX.
 *
 * This is the first place in LexPortal where the cascade is visible.
 */
export function MatterAssignments() {
  const { orgId = "", clientId = "", matterId = "" } = useParams<{
    orgId: string;
    clientId: string;
    matterId: string;
  }>();
  const api = usePlatformApi();

  const [client, setClient] = useState<Client | null>(null);
  const [matter, setMatter] = useState<Matter | null>(null);
  const [assignments, setAssignments] = useState<UserAssignment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!orgId || !clientId || !matterId) return;
    try {
      setLoading(true);
      const [clientRes, matterRes, matterRowsRes, clientRowsRes] = await Promise.all([
        api.getClient(orgId, clientId),
        api.getMatter(orgId, clientId, matterId),
        api.listMatterAssignments(orgId, clientId, matterId),
        api.listClientAssignments(orgId, clientId),
      ]);
      const clientName = clientRes.data.name;
      setClient(clientRes.data);
      setMatter(matterRes.data);

      const directRows = matterRowsRes.data.map(matterRowToUserAssignment);
      const inheritedRows = clientRowsRes.data
        // Filter out users who already have a direct matter grant: the
        // cascade resolver takes max(role), but showing the same user
        // twice in the UI is confusing. The direct row wins visually.
        .filter(
          (row) =>
            !matterRowsRes.data.some((mr) => mr.userId === row.userId),
        )
        .map((row) =>
          toInheritedFromClient(row, "matter", matterId, clientName),
        );

      setAssignments([...directRows, ...inheritedRows]);
      setError(null);
    } catch (e) {
      setError(
        e instanceof PlatformApiError
          ? `${e.code}: ${e.message}`
          : e instanceof Error
            ? e.message
            : "Failed to load matter assignments",
      );
    } finally {
      setLoading(false);
    }
  }, [api, orgId, clientId, matterId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCreate = async (input: CreateAssignmentInput) => {
    setSubmitting(true);
    setError(null);
    try {
      await api.createMatterAssignment(orgId, clientId, matterId, input);
      setShowCreate(false);
      await load();
    } catch (e) {
      setError(
        e instanceof PlatformApiError
          ? `${e.code}: ${e.message}`
          : e instanceof Error
            ? e.message
            : "Failed to create matter assignment",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateRole = async (userId: string, role: AssignmentRole) => {
    setError(null);
    try {
      await api.updateMatterAssignment(orgId, clientId, matterId, userId, { role });
      await load();
    } catch (e) {
      setError(
        e instanceof PlatformApiError
          ? `${e.code}: ${e.message}`
          : e instanceof Error
            ? e.message
            : "Failed to update assignment",
      );
    }
  };

  const handleDelete = async (userId: string) => {
    const ok = window.confirm(
      `Revoke matter assignment for user "${userId}"? They'll lose cascade access to every case under this matter.`,
    );
    if (!ok) return;
    setDeleteLoading(userId);
    setError(null);
    try {
      await api.deleteMatterAssignment(orgId, clientId, matterId, userId);
      await load();
    } catch (e) {
      setError(
        e instanceof PlatformApiError
          ? `${e.code}: ${e.message}`
          : e instanceof Error
            ? e.message
            : "Failed to revoke assignment",
      );
    } finally {
      setDeleteLoading(null);
    }
  };

  const parentMissing = !loading && (!client || !matter);
  if (parentMissing) {
    return (
      <div className="space-y-4" data-testid="matter-assignments-page">
        <Link
          to={`/orgs/${orgId}/clients`}
          className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
        >
          <ChevronLeft className="h-3 w-3" /> Back to clients
        </Link>
        <div
          role="alert"
          className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300"
          data-testid="matter-assignments-parent-not-found"
        >
          <p className="font-medium">Parent client or matter not found</p>
          <p className="mt-1 text-xs">{error ?? "Check the URL and try again."}</p>
        </div>
      </div>
    );
  }

  const directCount = assignments.filter((a) => a.source === "direct").length;

  return (
    <div className="space-y-6" data-testid="matter-assignments-page">
      <header className="flex items-center justify-between">
        <div>
          <Link
            to={`/orgs/${orgId}/clients/${clientId}/matters`}
            className="mb-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
          >
            <ChevronLeft className="h-3 w-3" /> Back to matters
          </Link>
          <h1 className="text-2xl font-semibold">
            {matter ? `Assignments — ${matter.name}` : "Matter assignments"}
          </h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {loading
              ? "Loading..."
              : `${directCount} direct / ${assignments.length - directCount} inherited from ${client?.name ?? clientId}`}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          disabled={!matter || loading}
          className="inline-flex items-center gap-2 rounded bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
          data-testid="matter-assignments-create-button"
        >
          <Plus className="h-4 w-4" />
          Grant role
        </button>
      </header>

      {error && (
        <div
          role="alert"
          className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-900/40 dark:bg-red-900/20 dark:text-red-300"
          data-testid="matter-assignments-error"
        >
          {error}
        </div>
      )}

      <div className="rounded border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
        <AssignmentList
          scope="matter"
          assignments={assignments}
          loading={loading}
          onUpdateRole={handleUpdateRole}
          onDelete={handleDelete}
          deleteLoading={deleteLoading}
        />
      </div>

      <Dialog open={showCreate} onOpenChange={(o) => !o && setShowCreate(false)}>
        <DialogContent data-testid="matter-assignments-create-dialog">
          <DialogHeader>
            <DialogTitle>Grant a matter role</DialogTitle>
            <DialogDescription>
              The user will gain cascade access to every case under{" "}
              {matter?.name ?? "this matter"}.
            </DialogDescription>
          </DialogHeader>
          <AssignmentForm
            scope="matter"
            onSubmit={handleCreate}
            onCancel={() => setShowCreate(false)}
            submitting={submitting}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
